import { useMemo } from 'react';
import { useAuth } from '@shared/auth/AuthContext';
import { allSuites } from '@suites/registry';

export interface MobileTab {
  id: string;
  label: string;
  icon: string;
  path: string;
  suite: string;
  permission?: string;
}

export const TAB_CANDIDATES: Record<string, MobileTab[]> = {
  admin: [
    { id: 'contacts', label: 'Contacts', icon: 'users', path: '/core/contacts', suite: 'core' },
    { id: 'sales', label: 'Deals', icon: 'trending-up', path: '/sales/essentials/deals-pipeline', suite: 'sales' },
    { id: 'finance', label: 'Invoices', icon: 'file-text', path: '/finance/invoicing/invoices', suite: 'finance' },
    { id: 'ops', label: 'Operations', icon: 'activity', path: '/operations/inventory/stock-levels', suite: 'operations' },
    { id: 'users', label: 'Users', icon: 'shield', path: '/core/users', suite: 'core' },
  ],
  sales: [
    { id: 'deals', label: 'Deals', icon: 'trending-up', path: '/sales/essentials/deals-pipeline', suite: 'sales' },
    { id: 'leads', label: 'Leads', icon: 'target', path: '/sales/essentials/leads', suite: 'sales' },
    { id: 'contacts', label: 'Contacts', icon: 'users', path: '/core/contacts', suite: 'core' },
    { id: 'quotes', label: 'Quotes', icon: 'file-check', path: '/sales/cpq/quote-builder', suite: 'sales' },
  ],
  finance: [
    { id: 'invoices', label: 'Invoices', icon: 'file-text', path: '/finance/invoicing/invoices', suite: 'finance' },
    { id: 'payments', label: 'Payments', icon: 'credit-card', path: '/finance/invoicing/payments', suite: 'finance' },
    { id: 'gl', label: 'GL Ledger', icon: 'book-open', path: '/finance/gl/chart-of-accounts', suite: 'finance' },
    { id: 'expenses', label: 'Expenses', icon: 'receipt', path: '/finance/expenses/reports', suite: 'finance' },
  ],
  projects: [
    { id: 'projects', label: 'Projects', icon: 'briefcase', path: '/projects/management/projects', suite: 'projects' },
    { id: 'tasks', label: 'Tasks', icon: 'check-square', path: '/projects/management/tasks', suite: 'projects' },
    { id: 'time', label: 'Time Logs', icon: 'clock', path: '/projects/time/time-entries', suite: 'projects' },
    { id: 'gantt', label: 'CPM Gantt', icon: 'bar-chart-2', path: '/projects/management/gantt', suite: 'projects' },
  ],
  people: [
    { id: 'employees', label: 'Employees', icon: 'users', path: '/people/core/employees', suite: 'people' },
    { id: 'leaves', label: 'Time Off', icon: 'calendar', path: '/people/attendance/attendance', suite: 'people' },
    { id: 'payroll', label: 'Payroll', icon: 'dollar-sign', path: '/people/payroll/payroll-run', suite: 'people' },
    { id: 'profile', label: 'My Profile', icon: 'user', path: '/people/selfservice/ess-profile', suite: 'people' },
  ],
  support: [
    { id: 'tickets', label: 'Tickets', icon: 'inbox', path: '/support/tickets/tickets', suite: 'support' },
    { id: 'sla', label: 'SLA', icon: 'sliders', path: '/support/advanced-tickets/sla', suite: 'support' },
    { id: 'kb', label: 'KB Articles', icon: 'book', path: '/support/knowledge/kb-articles', suite: 'support' },
    { id: 'csat', label: 'CSAT', icon: 'check', path: '/support/feedback/csat-surveys', suite: 'support' },
  ],
};

export const MORE_TAB: MobileTab = {
  id: 'more',
  label: 'More',
  icon: 'layout-grid',
  path: '#more',
  suite: '',
};

export function useMobileTabs(): MobileTab[] {
  const { user, enabledSuites, hasPermission } = useAuth();

  return useMemo(() => {
    const roleKey = (user?.role || 'admin').toLowerCase();
    const candidates = TAB_CANDIDATES[roleKey] || TAB_CANDIDATES.admin;

    const filtered = candidates.filter((tab) => {
      if (enabledSuites && enabledSuites.length > 0 && !enabledSuites.includes(tab.suite)) {
        return false;
      }
      const manifest = allSuites.find((s) => s.id === tab.suite);
      if (!manifest) return false;
      if (tab.permission && typeof hasPermission === 'function' && !hasPermission(tab.permission)) {
        return false;
      }
      return true;
    });

    const finalTabs: MobileTab[] = [...filtered.slice(0, 4)];
    if (finalTabs.length < 3) {
      for (const candidate of TAB_CANDIDATES.admin) {
        if (!finalTabs.some((t) => t.id === candidate.id)) {
          finalTabs.push(candidate);
          if (finalTabs.length >= 3) break;
        }
      }
    }

    return [...finalTabs.slice(0, 4), MORE_TAB];
  }, [user?.role, enabledSuites, hasPermission]);
}
